/**
 * 9 Venture Slots — фиксированные контракты Luna Protokol.
 *
 * Источник: ТЗ `/luna-protokol/TZ.html`. Открываются поэтапно:
 *   Месяц 1: Photon / Comet / Asteroid  (базовая ~12%/мес)
 *   Месяц 2: Lunar / Solar / Nebula     (~12.5%/мес)
 *   Месяц 3: Quasar / Pulsar / Supernova (~13%/мес, байт на максимум)
 *
 * Каждый слот имеет лимит 250% total payout (потом сгорает).
 * Нельзя внести произвольную сумму — только фиксированные amounts.
 * Доход идёт в самый старый активный слот.
 */

import photonIcon from '@/assets/slot-icons/photon.webp';
import cometIcon from '@/assets/slot-icons/comet.webp';
import asteroidIcon from '@/assets/slot-icons/asteroid.webp';
import lunarIcon from '@/assets/slot-icons/lunar.webp';
import solarIcon from '@/assets/slot-icons/solar.webp';
import nebulaIcon from '@/assets/slot-icons/nebula.webp';
import quasarIcon from '@/assets/slot-icons/quasar.webp';
import pulsarIcon from '@/assets/slot-icons/pulsar.webp';
import supernovaIcon from '@/assets/slot-icons/supernova.webp';

export type SlotRating = 'минимальная' | 'низкая' | 'средняя' | 'высокая' | 'отличная' | 'максимальная' | 'премиум' | 'лучшая';

export interface VentureSlotInfo {
  id: string;
  /** Числовой ID из luna_venture_slot_configs (1-9) */
  dbId: number;
  name: string;
  /** Fixed deposit amount in USDT */
  amount: number;
  /** Monthly rate in % (approximate, can fluctuate ±5%) */
  monthlyRate: number;
  /** Daily rate in %, derived from monthlyRate/30 */
  dailyRate: number;
  /** Month when this slot is unlocked (1, 2 or 3) */
  unlockMonth: 1 | 2 | 3;
  /** Subjective rating from TZ */
  rating: SlotRating;
  /** HSL hue for the slot's accent colour / glow */
  hue: number;
  /** Tagline for display */
  tagline: string;
  /** Generated WebP icon */
  icon: string;
}

export const VENTURE_SLOTS: readonly VentureSlotInfo[] = [
  // ─── МЕСЯЦ 1 — точка входа ──────────────────────────────────
  {
    id: 'photon',
    dbId: 1,
    name: 'Photon',
    amount: 100,
    monthlyRate: 12,
    dailyRate: 0.40,
    unlockMonth: 1,
    rating: 'минимальная',
    hue: 280,
    tagline: 'Квант импульса',
    icon: photonIcon,
  },
  {
    id: 'comet',
    dbId: 2,
    name: 'Comet',
    amount: 300,
    monthlyRate: 12,
    dailyRate: 0.40,
    unlockMonth: 1,
    rating: 'низкая',
    hue: 195,
    tagline: 'Первая траектория',
    icon: cometIcon,
  },
  {
    id: 'asteroid',
    dbId: 3,
    name: 'Asteroid',
    amount: 500,
    monthlyRate: 12,
    dailyRate: 0.40,
    unlockMonth: 1,
    rating: 'средняя',
    hue: 35,
    tagline: 'Рабочий массив',
    icon: asteroidIcon,
  },

  // ─── МЕСЯЦ 2 — масштаб ──────────────────────────────────────
  {
    id: 'lunar',
    dbId: 4,
    name: 'Lunar',
    amount: 1_000,
    monthlyRate: 12.5,
    dailyRate: 0.42,
    unlockMonth: 2,
    rating: 'высокая',
    hue: 220,
    tagline: 'Лунная гравитация',
    icon: lunarIcon,
  },
  {
    id: 'solar',
    dbId: 5,
    name: 'Solar',
    amount: 2_000,
    monthlyRate: 12.5,
    dailyRate: 0.42,
    unlockMonth: 2,
    rating: 'отличная',
    hue: 48,
    tagline: 'Термоядерный поток',
    icon: solarIcon,
  },
  {
    id: 'nebula',
    dbId: 6,
    name: 'Nebula',
    amount: 5_000,
    monthlyRate: 12.5,
    dailyRate: 0.42,
    unlockMonth: 2,
    rating: 'отличная',
    hue: 280,
    tagline: 'Кластер плотности',
    icon: nebulaIcon,
  },

  // ─── МЕСЯЦ 3 — премиальные контракты ────────────────────────
  {
    id: 'quasar',
    dbId: 7,
    name: 'Quasar',
    amount: 10_000,
    monthlyRate: 13,
    dailyRate: 0.43,
    unlockMonth: 3,
    rating: 'максимальная',
    hue: 310,
    tagline: 'Пик мощности',
    icon: quasarIcon,
  },
  {
    id: 'pulsar',
    dbId: 8,
    name: 'Pulsar',
    amount: 25_000,
    monthlyRate: 13,
    dailyRate: 0.43,
    unlockMonth: 3,
    rating: 'премиум',
    hue: 210,
    tagline: 'Когерентный луч',
    icon: pulsarIcon,
  },
  {
    id: 'supernova',
    dbId: 9,
    name: 'Supernova',
    amount: 50_000,
    monthlyRate: 13,
    dailyRate: 0.43,
    unlockMonth: 3,
    rating: 'лучшая',
    hue: 42,
    tagline: 'Флагманский контракт',
    icon: supernovaIcon,
  },
] as const;
